/**
 * documentDownload — fetches the raw bytes of a SiteDocument through
 * the server's /api/storage/download-file proxy and triggers a
 * browser save dialog with the original filename.
 *
 * The proxy is necessary because GCS objects are private — the
 * server attaches the service-account bearer token before forwarding
 * the upstream stream to the client. authedFetch carries the user's
 * Firebase ID token so the endpoint's requireFirebaseAuth gate
 * passes.
 */
// file-saver@2 is a CJS module whose `module.exports` IS the saveAs function;
// Node's ESM loader can't synthesize a named `saveAs` export (it works under
// Vite/esbuild but throws under `tsx server.ts`). The default import resolves
// to module.exports (the function) in every runtime.
import saveAs from 'file-saver';
import { authedFetch } from './authedFetch';
import type { SiteDocument } from '../types/siteRag';
import type { DocumentBinaryLoader } from './siteDocumentOriginalClient';

export async function downloadDocument(doc: SiteDocument, binaryLoader?: DocumentBinaryLoader): Promise<void> {
  if (!doc.rawStoragePath) {
    throw new Error(
      'This document was uploaded before raw-file persistence — re-upload to enable download.',
    );
  }
  if (binaryLoader) {
    saveAs(await binaryLoader(doc), doc.fileName);
    return;
  }
  const res = await authedFetch(
    `/api/storage/download-file?path=${encodeURIComponent(doc.rawStoragePath)}`,
  );
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  saveAs(blob, doc.fileName);
}
